import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatbotPersonality, MediaAsset } from '../types/chatbot';
import ProfileContent from './ProfileContent';
import Badge from './ui/Badge';
import OptimizedVideo from './ui/OptimizedVideo';

// Helper function to convert companion name to URL-safe slug
const nameToSlug = (name: string): string => {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
};

interface ChatbotStats {
  likeCount: number;
  chatCount: number;
  isLiked: boolean;
  hasStartedChat: boolean;
}

interface ChatbotProfileProps {
  chatbot: ChatbotPersonality;
  onBack: () => void;
  onStartChat: (chatbot: ChatbotPersonality) => void;
  chatbotStats?: ChatbotStats;
  onUpdateStats: (stats: Partial<ChatbotStats>) => void;
}

const ChatbotProfile: React.FC<ChatbotProfileProps> = ({
  chatbot,
  onBack,
  onStartChat,
  chatbotStats,
  onUpdateStats
}) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Touch/swipe state for navigation
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Lock body scroll when component mounts, unlock when unmounts
  React.useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, []);

  // Use new galleryAssets if available, fallback to old gallery format
  const galleryAssets = React.useMemo(() => chatbot.galleryAssets || [], [chatbot.galleryAssets]);
  const images = React.useMemo(() => chatbot.gallery || [chatbot.avatar].filter(Boolean) as string[], [chatbot.gallery, chatbot.avatar]);

  // Combine assets for the gallery (prioritize new format)
  const mediaAssets: MediaAsset[] = React.useMemo(() => {
    if (galleryAssets.length > 0) {
      return galleryAssets.map(asset => {
        if (asset.type === 'video') {
          const videoFileName = asset.url.split('/').pop()?.split('?')[0] || '';
          const posterFileName = videoFileName.replace('.mp4', '_poster.jpg');

          const matchingPoster = galleryAssets.find(posterAsset =>
            posterAsset.type === 'image' &&
            posterAsset.url.includes(posterFileName)
          );

          if (matchingPoster) {
            return { ...asset, poster: matchingPoster.url };
          }
        }
        return asset;
      });
    }
    return images.map(url => ({ url, type: 'image' as const, id: url }));
  }, [galleryAssets, images]);

  const isLiked = chatbotStats?.isLiked || false;
  const likeCount = chatbotStats?.likeCount || 0;
  const chatCount = chatbotStats?.chatCount || 0;
  const hasStartedChat = chatbotStats?.hasStartedChat || false;

  const nextImage = () => {
    const totalItems = mediaAssets.length > 0 ? mediaAssets.length : images.length;
    setCurrentImageIndex((prev) => (prev + 1) % totalItems);
  };

  const prevImage = () => {
    const totalItems = mediaAssets.length > 0 ? mediaAssets.length : images.length;
    setCurrentImageIndex((prev) => (prev - 1 + totalItems) % totalItems);
  };

  // Swipe detection
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    const totalItems = mediaAssets.length > 0 ? mediaAssets.length : images.length;

    if (isLeftSwipe && totalItems > 1) {
      nextImage();
    }
    if (isRightSwipe && totalItems > 1) {
      prevImage();
    }
  };

  const handleLikeClick = () => {
    if (isLiked) {
      onUpdateStats({
        isLiked: false,
        likeCount: likeCount - 1,
      });
    } else {
      onUpdateStats({
        isLiked: true,
        likeCount: likeCount + 1,
      });
    }
  };

  const handleChatClick = () => {
    onUpdateStats({
      hasStartedChat: true,
      chatCount: chatCount + 1,
    });

    // Navigate to the new URL structure
    const companionSlug = nameToSlug(chatbot.name);
    const companionId = chatbot.id; // This should be the companion_id
    navigate(`/conversations/${companionSlug}/${companionId}`);
  };

  const handleGenerate = () => {
    console.log('Generate clicked for', chatbot.name);
  };


  // Removed old renderProfileContent function - now using shared ProfileContent component

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70]">
      {/* Mobile Layout - Scrollable */}
      <div className="lg:hidden w-full h-full overflow-y-auto">
        <div className="min-h-full bg-dark-900">
          {/* Large Image Section for Mobile */}
          <div 
            className="relative w-full h-[70vh] overflow-hidden"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {(mediaAssets.length > 0 || images.length > 0) ? (
              <div className="relative w-full h-full">
                <div className="w-full h-full overflow-hidden">
                  {mediaAssets.length > 0 && mediaAssets[currentImageIndex]?.type === 'video' ? (
                    <OptimizedVideo
                      src={mediaAssets[currentImageIndex].url}
                      poster={(mediaAssets[currentImageIndex] as any).poster}
                      className="w-full h-full"
                      autoPlay={true}
                      muted={true}
                      loop={true}
                      controls={false}
                      priority={true}
                    />
                  ) : (
                    <img
                      src={mediaAssets.length > 0 ? mediaAssets[currentImageIndex]?.url : (images.length > 0 ? images[currentImageIndex] : '')}
                      alt={`${chatbot.name} ${currentImageIndex + 1}`}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  )}
                </div>

                {/* Navigation arrows for mobile */}
                {(mediaAssets.length > 1 || images.length > 1) && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}

                {/* Media counter for mobile */}
                {(mediaAssets.length > 1 || images.length > 1) && (
                  <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-sm text-white text-sm px-3 py-1 rounded-full">
                    {currentImageIndex + 1} / {mediaAssets.length > 0 ? mediaAssets.length : images.length}
                  </div>
                )}

                {/* Close button for mobile */}
                <button
                  onClick={onBack}
                  className="absolute top-4 right-4 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-accent-purple to-accent-pink flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="text-6xl mb-4">✨</div>
                  <div className="text-xl font-semibold">{chatbot.name}</div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Profile Content - Scrollable */}
          <div className="p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-white">{chatbot.name.toUpperCase()}</h1>
                <span className="text-xl font-semibold text-accent-pink">
                  {chatbot.age || '?'}
                </span>
              </div>
              <Badge variant="success" className="text-xs">
                <div className="w-2 h-2 bg-accent-purple rounded-full mr-2 animate-pulse"></div>
                Online
              </Badge>
            </div>

            {/* Profile Content */}
            <ProfileContent
              chatbot={chatbot}
              chatbotStats={chatbotStats}
              onUpdateStats={onUpdateStats}
              onStartChat={handleChatClick}
              showChatButton={true}
              layout="modal"
            />

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Chat Button */}
              <button
                onClick={handleChatClick}
                className="w-full py-3 bg-gradient-to-r from-accent-purple to-accent-pink hover:shadow-lg hover:shadow-accent-purple/25 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {hasStartedChat ? 'Continue Chat' : 'Start Chat'}
              </button>

              {/* Action Row */}
              <div className="flex gap-3">
                <button
                  onClick={handleLikeClick}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${isLiked
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                    : 'bg-dark-700 text-dark-300 border border-dark-600 hover:bg-dark-600 hover:text-white'
                    }`}
                >
                  <svg className="w-4 h-4" fill={isLiked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  {likeCount}
                </button>

                <button
                  onClick={handleGenerate}
                  className="flex-1 py-2 px-4 bg-dark-700 text-dark-300 border border-dark-600 rounded-lg font-medium hover:bg-dark-600 hover:text-white transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Generate
                </button>
              </div>

              {/* Stats */}
              <div className="flex items-center justify-center gap-6 text-sm text-dark-400">
                <div className="flex items-center gap-1">
                  <span className="text-red-400">❤️</span>
                  <span>{likeCount} likes</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-blue-400">💬</span>
                  <span>{chatCount} chats</span>
                </div>
              </div>
            </div>

            {/* Minimal spacing to clear navigation bar */}
            <div className="pb-20"></div>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:flex items-center justify-center p-4">
        <div className="w-full max-w-6xl max-h-[90vh] mx-auto bg-dark-900 flex rounded-2xl shadow-2xl overflow-hidden border border-dark-600">
          {/* Left side - Media */}
          <div 
            className="w-1/2 relative overflow-hidden"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {(mediaAssets.length > 0 || images.length > 0) ? (
              <>
                {mediaAssets.length > 0 && mediaAssets[currentImageIndex]?.type === 'video' ? (
                  <OptimizedVideo
                    src={mediaAssets[currentImageIndex].url}
                    poster={(mediaAssets[currentImageIndex] as any).poster}
                    className="w-full h-full"
                    autoPlay={true}
                    muted={true}
                    loop={true}
                    controls={false}
                    priority={true}
                  />
                ) : (
                  <img
                    src={mediaAssets.length > 0 ? mediaAssets[currentImageIndex]?.url : (images.length > 0 ? images[currentImageIndex] : '')}
                    alt={`${chatbot.name} ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}

                {/* Navigation arrows */}
                {(mediaAssets.length > 1 || images.length > 1) && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}

                {/* Media counter */}
                {(mediaAssets.length > 1 || images.length > 1) && (
                  <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-sm text-white text-sm px-3 py-1 rounded-full">
                    {currentImageIndex + 1} / {mediaAssets.length > 0 ? mediaAssets.length : images.length}
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-accent-purple to-accent-pink flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="text-6xl mb-4">✨</div>
                  <div className="text-xl font-semibold">{chatbot.name}</div>
                </div>
              </div>
            )}
          </div>

          {/* Right side - Profile Info (Desktop) */}
          <div className="w-1/2 p-6 flex flex-col overflow-hidden rounded-r-2xl">
            {/* Header with name, age and close button */}
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-white">{chatbot.name.toUpperCase()}</h1>
                <span className="text-xl font-semibold text-accent-pink">
                  {chatbot.age || '?'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="success" className="text-xs">
                  <div className="w-2 h-2 bg-accent-purple rounded-full mr-2 animate-pulse"></div>
                  Online
                </Badge>
                <button
                  onClick={onBack}
                  className="text-dark-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto pr-2 space-y-4">
              <ProfileContent
                chatbot={chatbot}
                chatbotStats={chatbotStats}
                onUpdateStats={onUpdateStats}
                onStartChat={handleChatClick}
                showChatButton={true}
                layout="modal"
              />
            </div>

            {/* Action Buttons - Fixed at bottom */}
            <div className="flex-shrink-0 space-y-3 pt-4 border-t border-dark-600">
              <button
                onClick={handleChatClick}
                className="w-full py-3 bg-gradient-to-r from-accent-purple to-accent-pink hover:shadow-lg hover:shadow-accent-purple/25 text-white rounded-lg font-medium transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {hasStartedChat ? 'Continue Chat' : 'Start Chat'}
              </button>

              <div className="flex gap-3">
                <button
                  onClick={handleLikeClick}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 ${isLiked
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                    : 'bg-dark-700 text-dark-300 border border-dark-600 hover:bg-dark-600 hover:text-white'
                    }`}
                >
                  <svg className="w-4 h-4" fill={isLiked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  {likeCount}
                </button>

                <button
                  onClick={handleGenerate}
                  className="flex-1 py-2 px-4 bg-dark-700 text-dark-300 border border-dark-600 rounded-lg font-medium hover:bg-dark-600 hover:text-white transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Generate
                </button>
              </div>

              <div className="flex items-center justify-center gap-6 text-sm text-dark-400">
                <div className="flex items-center gap-1">
                  <span className="text-red-400">❤️</span>
                  <span>{likeCount} likes</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-blue-400">💬</span>
                  <span>{chatCount} chats</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotProfile;